/**
 * The backoffice OtterAuth client (04-architecture § 11, 10-web-admin § 2).
 *
 * Identical construction to the member app — the accounts are ordinary
 * OtterAuth users and the role comes from our API (`GET /admin/me`). What differs
 * is what happens afterwards: the gate, not a funnel.
 */

import { createAuth, localStorageTokenStorage } from '@cowoo/shared';
import { env } from '../env';
import { api } from './api';
import { useAdminStore } from './store';

export const auth = createAuth({
  publishableKey: env.OTTER_PUBLISHABLE_KEY ?? '',
  // § 5.4 — the key the server serves wins; the build-time one is the fallback.
  runtimeConfig: async () => {
    const { data, error } = await api.GET('/meta');
    if (error || !data) throw error ?? new Error('meta unavailable');
    return data.otter;
  },
  storage: localStorageTokenStorage(),
  onSessionInvalidated: () => useAdminStore.getState().clear(),
});

/** § 2 — the redirect URI registered for this app (06-auth § 1). */
export const OAUTH_REDIRECT_URI = `${env.ADMIN_URL}/auth/callback`;

/**
 * A token that survived the reload is adopted before the first render, so the
 * gate does not bounce a signed-in admin to `/login` on the way past. The role
 * comes from `GET /admin/me`, which the gate runs itself.
 */
export function hydrateSession(): void {
  const token = auth.getToken();
  if (token) useAdminStore.getState().setSession(token);
}

/** § 11.5 — drop the server's session cache first, then the SDK's token. */
export async function signOut(logoutRequest: () => Promise<unknown>): Promise<void> {
  try {
    await logoutRequest();
  } catch {
    // A failed cache drop must not strand the user in a signed-in shell; the
    // entry expires in 60 s anyway.
  }
  await auth.logout();
  useAdminStore.getState().clear();
}
